import type { ColDef, ColGroupDef } from 'ag-grid-community'

import AnalyticsKpisBuilder from './AnalyticsKpisBuilder'
import { baseColumnsDefs } from './base-columns'
import ApplicationCellRenderer from './CellRenderers/ApplicationCellRenderer'
import HintCellRenderer from './CellRenderers/HintCellRenderer'
import {
  fieldsWithMoneyFormatter,
  fieldsWithNumberValueFormatter,
  fieldsWithPercentValueFormatter,
  fieldsWithSparklineNegativeValueFormatter,
  fieldsWithSparklinePositiveValueFormatter,
} from './Options'
import { formatMoney, formatNegativeYValue, formatNumber, formatPercent, formatPositiveYValue } from './utils'

import CountryCellRenderer from '~/components/CellRenderers/CountryCellRenderer'
import DeviceTypeCellRenderer from '~/components/CellRenderers/DeviceTypeCellRenderer'
import { LOCAL_STORAGE } from '~/const'

const columnDefs = (aggregations: string[]): ColDef[] => {
  let defs: ColDef[] = []

  if (aggregations) {
    aggregations.forEach((aggreg, i) => {
      let payload: ColDef | null = null

      if (aggreg === 'app_key') {
        payload = {
          cellRenderer: ApplicationCellRenderer,
          field: 'app_key',
          filter: true,
          headerName: 'App Key',
          hide: false,
          sortable: true,
        }
      }
      if (aggreg === 'device_type') {
        payload = {
          cellRenderer: DeviceTypeCellRenderer,
          cellStyle: {
            overflow: 'unset',
          },
          field: 'device_type',
          filter: true,
          headerName: 'Platform',
          hide: false,
          minWidth: 100,
          sortable: true,
        }
      }
      if (aggreg === 'country') {
        payload = {
          cellRenderer: CountryCellRenderer,
          field: 'country',
          filter: true,
          headerName: 'Country',
          hide: false,
          sortable: true,
        }
      }
      if (aggreg === 'ua_source') {
        payload = {
          field: 'ua_source',
          filter: true,
          headerName: 'UA Source',
          hide: false,
          sortable: true,
        }
      }
      if (aggreg === 'ua_campaign_name') {
        payload = {
          field: 'ua_campaign_name',
          filter: true,
          headerName: 'UA Campaign',
          hide: false,
          sortable: true,
        }
      }
      if (aggreg === 'ua_adset_name') {
        payload = {
          field: 'ua_adset_name',
          filter: true,
          headerName: 'UA Adset',
          hide: false,
          sortable: true,
        }
      }
      if (aggreg === 'date') {
        payload = {
          field: 'date',
          filter: true,
          headerName: 'Date',
          hide: false,
          sort: 'asc',
          sortable: true,
        }
      }
      if (aggreg === 'week') {
        payload = {
          field: 'week',
          filter: true,
          headerName: 'Week',
          hide: false,
          sort: 'asc',
          sortable: true,
        }
      }
      if (aggreg === 'month') {
        payload = {
          field: 'month',
          filter: true,
          headerName: 'Month',
          hide: false,
          sort: 'asc',
          sortable: true,
        }
      }

      if (payload != null) {
        if (i < aggregations.length - 1) {
          payload.rowGroup = true
          payload.hide = true
          payload.rowGroupIndex = i
        }
        payload.pinned = 'left'
        defs.push(payload)
      }
    })
  }

  const columnsFromLocalStorage = localStorage.getItem(LOCAL_STORAGE.UA_DASHBOARD_COLUMNS)

  if (columnsFromLocalStorage) {
    const savedDefs = JSON.parse(columnsFromLocalStorage)
    savedDefs.forEach((colDef: ColGroupDef | ColDef) => {
      if ('children' in colDef) {
        colDef.children.forEach((child: ColDef) => {
          if (child.colId) {
            if (fieldsWithNumberValueFormatter.includes(child.colId)) {
              child.valueFormatter = formatNumber
            } else if (fieldsWithPercentValueFormatter.includes(child.colId)) {
              child.valueFormatter = formatPercent
            } else if (fieldsWithMoneyFormatter.includes(child.colId)) {
              child.valueFormatter = formatMoney
            }

            if (fieldsWithSparklinePositiveValueFormatter.includes(child.colId)) {
              child.cellRendererParams.sparklineOptions.formatter = formatPositiveYValue
              child.cellRendererParams.sparklineOptions.label.formatter = formatPercent
            }
            if (fieldsWithSparklineNegativeValueFormatter.includes(child.colId)) {
              child.cellRendererParams.sparklineOptions.formatter = formatNegativeYValue
              child.cellRendererParams.sparklineOptions.label.formatter = formatPercent
            }
            if (child.colId === 'view_more') {
              child.cellRenderer = 'viewMoreCellRenderer'
            }
            if (child.colId === 'hint') {
              child.cellRenderer = HintCellRenderer
            }
          }

          child.valueGetter = AnalyticsKpisBuilder.buildKpi
        })
      } else {
        if (colDef.colId) {
          if (fieldsWithNumberValueFormatter.includes(colDef.colId)) {
            colDef.valueFormatter = formatNumber
          } else if (fieldsWithPercentValueFormatter.includes(colDef.colId)) {
            colDef.valueFormatter = formatPercent
          } else if (fieldsWithMoneyFormatter.includes(colDef.colId)) {
            colDef.valueFormatter = formatMoney
          }

          if (fieldsWithSparklinePositiveValueFormatter.includes(colDef.colId)) {
            colDef.cellRendererParams.sparklineOptions.formatter = formatPositiveYValue
            colDef.cellRendererParams.sparklineOptions.label.formatter = formatPercent
          }
          if (fieldsWithSparklineNegativeValueFormatter.includes(colDef.colId)) {
            colDef.cellRendererParams.sparklineOptions.formatter = formatNegativeYValue
            colDef.cellRendererParams.sparklineOptions.label.formatter = formatPercent
          }
          if (colDef.colId === 'view_more') {
            colDef.cellRenderer = 'viewMoreCellRenderer'
          }
          if (colDef.colId === 'hint') {
            colDef.cellRenderer = HintCellRenderer
          }
        }

        colDef.valueGetter = AnalyticsKpisBuilder.buildKpi
      }
    })
    defs = defs.concat(savedDefs)
  } else {
    defs = defs.concat(baseColumnsDefs)
  }
  return defs
}

export default columnDefs
